import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AboutPage } from '@/features/about/AboutPage';
import { InvitePage } from '@/features/auth/InvitePage';
import { ProfilePage } from '@/features/auth/ProfilePage';
import { LoginPage } from '@/features/auth/LoginPage';
import { AccessLogTab } from '@/features/admin/AccessLogTab';
import { ActivationTab } from '@/features/admin/ActivationTab';
import { ContactsTab } from '@/features/admin/ContactsTab';
import { DataTab } from '@/features/admin/DataTab';
import { FeedbackTab } from '@/features/admin/FeedbackTab';
import { LearnedTab } from '@/features/admin/LearnedTab';
import { ReliabilityTab } from '@/features/admin/ReliabilityTab';
import { ErrorsTab } from '@/features/admin/ErrorsTab';
import { DocumentsTab } from '@/features/admin/DocumentsTab';
import { AdminPage } from '@/features/admin/AdminPage';
import { DestinationsTab } from '@/features/admin/DestinationsTab';
import { MailboxesTab } from '@/features/admin/MailboxesTab';
import { UsersTab } from '@/features/admin/UsersTab';
import { ActivationPage } from '@/features/activation/ActivationPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { EmployeesPage } from '@/features/payroll/EmployeesPage';
import { PayrollBatchPage } from '@/features/payroll/PayrollBatchPage';
import { PayrollPage } from '@/features/payroll/PayrollPage';
import { EmailDetailPage } from '@/features/emails/EmailDetailPage';
import { EmailsPage } from '@/features/emails/EmailsPage';
import { QuoteEditorPage } from '@/features/quotes/QuoteEditorPage';
import { QuotesPage } from '@/features/quotes/QuotesPage';
import { StatsTab } from '@/features/stats/StatsTab';
import { CatalogViewerPage } from '@/features/suppliers/CatalogViewerPage';
import { DocumentsHome } from '@/features/suppliers/DocumentsHome';
import { SupplierPage } from '@/features/suppliers/SupplierPage';
import { VersionPage } from '@/features/suppliers/VersionPage';
import { QUOTE_CAPABILITIES } from '@/shared/session/profiles';
import { ScreenErrorBoundary } from '@/shared/ui/ScreenErrorBoundary';

import { AppLayout } from './layout/AppLayout';
import { RequireAdmin, RequirePlatform } from './layout/RequireAdmin';
import { RequireAuth } from './layout/RequireAuth';
import { RequireCapability } from './layout/RequireCapability';
import { RequireFeature } from './layout/RequireFeature';
import { RequirePayroll } from './layout/RequirePayroll';
import { AppProviders } from './providers';

/**
 * Route table.
 *
 * Exported without the router on purpose: the tests mount it inside a MemoryRouter to
 * drive navigation without a browser.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      {/* The invitation link (contract v10.10): public, the person has no session yet. */}
      <Route path="/invitacion/:token" element={<InvitePage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/correos" replace />} />
          <Route path="/correos" element={<EmailsPage />} />
          <Route path="/correos/:emailId" element={<EmailDetailPage />} />
          {/* Contract v10.47 (ADR 0042 del back): by profile. The API refuses the area to
              anybody else regardless. */}
          <Route element={<RequireCapability capability={QUOTE_CAPABILITIES} />}>
            <Route path="/presupuestos" element={<QuotesPage />} />
            <Route path="/presupuestos/:quoteId" element={<QuoteEditorPage />} />
          </Route>
          <Route element={<RequireCapability capability="clients" />}>
            <Route path="/clientes" element={<ClientsPage />} />
          </Route>

          {/* Contract v10.48 (ADR 0043 del back): the suppliers, their conditions and
              their tariffs, with the search box. What a profile may not see of the prices
              comes empty from the API. */}
          <Route element={<RequireCapability capability="documents" />}>
            <Route path="/documentos" element={<DocumentsHome />} />
            <Route path="/documentos/proveedores/:supplierId" element={<SupplierPage />} />
            <Route path="/documentos/tarifas/:versionId" element={<VersionPage />} />
            {/* Contract v10.49 (ADR 0044 del back): a supplier's catalog, read in pieces. */}
            <Route path="/documentos/catalogos/:catalogId" element={<CatalogViewerPage />} />
          </Route>

          {/* Fase 1 (ADR 0016 del back): apagado por defecto. Sin la funcion, va a la
              bandeja igual que una ruta desconocida. Was /documentos until contract v10.48,
              which gave that name to the supplier documents. */}
          <Route element={<RequireFeature feature="document_archive" />}>
            {/* Approved quotes and supplier offers: what Cubas pays (contract v10.47). */}
            <Route element={<RequireCapability capability="see_purchase" />}>
              <Route path="/archivo" element={<DocumentsPage />} />
            </Route>
          </Route>

          {/* Contract v10.53 (ADR 0047 del back): the payroll, for whoever has its own
              permission. Not «Administra», not a superadministrator. */}
          <Route element={<RequirePayroll />}>
            <Route path="/nominas" element={<PayrollPage />} />
            <Route path="/nominas/trabajadores" element={<EmployeesPage />} />
            <Route path="/nominas/:batchId" element={<PayrollBatchPage />} />
          </Route>

          <Route path="/acerca-de" element={<AboutPage />} />
          <Route path="/perfil" element={<ProfilePage />} />

          {/* Only an administrator (Ignira or the is_admin switch, contract v10.11), and
              the API refuses the data to anybody else regardless: the guard is for the
              user, not for security. */}
          <Route element={<RequireAdmin />}>
            <Route path="/activacion" element={<ActivationPage />} />
            <Route path="/administracion" element={<AdminPage />}>
              <Route index element={<Navigate to="/administracion/usuarios" replace />} />
              <Route path="usuarios" element={<UsersTab />} />
              {/* Contract v10.58: the card of one person is a link. */}
              <Route path="usuarios/:userId" element={<UsersTab />} />
              <Route path="buzones" element={<MailboxesTab />} />
              <Route path="destinos" element={<DestinationsTab />} />
              {/* Contract v10.44: the address book and its groups, for whoever administers. */}
              <Route path="contactos" element={<ContactsTab />} />
              <Route path="activacion" element={<ActivationTab />} />
              {/* Contract v10.35: for whoever administers, who wrote the notes. */}
              <Route path="aprendido" element={<LearnedTab />} />
              {/* The platform half: Ignira alone. */}
              <Route element={<RequirePlatform />}>
                <Route path="accesos" element={<AccessLogTab />} />
                <Route path="datos" element={<DataTab />} />
                <Route path="correcciones" element={<FeedbackTab />} />
                <Route path="incidencias" element={<ErrorsTab />} />
                <Route path="fiabilidad" element={<ReliabilityTab />} />
                <Route path="estadisticas" element={<StatsTab />} />
                <Route element={<RequireFeature feature="document_archive" />}>
                  <Route path="documentos" element={<DocumentsTab />} />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Estadísticas lives in Administración since 2026-10-02: old links land there. */}
          <Route
            path="/estadisticas"
            element={<Navigate to="/administracion/estadisticas" replace />}
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/correos" replace />} />
    </Routes>
  );
}

export function AppRouter() {
  return (
    <ScreenErrorBoundary page>
      <AppProviders>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AppProviders>
    </ScreenErrorBoundary>
  );
}
